/**
 * Read-only mode for the admin area — the Viewer role (ADR 0072).
 *
 * A viewer opens the admin area to look, not to change. `AdminPage` sets
 * `adminReadOnly` from the caller's profile; every control that writes
 * carries `v-write`, which turns it inert while the flag is on and says why
 * in a tooltip.
 *
 * Why a directive and not `:disabled` on each button: many of these buttons
 * already bind `disabled` to their own busy state, and a directive that
 * re-applies after every patch composes with that without touching the
 * expression. Why the click is blocked rather than the button `disabled`:
 * browsers swallow hover on disabled buttons, so the "Read-only role"
 * tooltip would never show.
 *
 * None of this is the security boundary. The backend refuses every write
 * from a viewer with 403 whatever the page does.
 */
import { ref, watchEffect, type Directive, type WatchStopHandle } from "vue";

export const READ_ONLY_TITLE = "Read-only role";

/** True while the signed-in person is in the admin area on the Viewer role. */
export const adminReadOnly = ref(false);

/** `admin.read` without `admin.write`: the Viewer role, as the SPA sees it. */
export function isReadOnlyProfile(
  p: { is_admin: boolean; permissions?: string[] } | null | undefined,
): boolean {
  if (!p || p.is_admin) return false;
  return (p.permissions ?? []).includes("admin.read");
}

/** May open the admin area at all (admin, or viewer). */
export function canOpenAdmin(
  p: { is_admin: boolean; permissions?: string[] } | null | undefined,
): boolean {
  return !!p && (p.is_admin || (p.permissions ?? []).includes("admin.read"));
}

type Guarded = HTMLElement & {
  __writeStop?: WatchStopHandle;
  __writeBlock?: (e: Event) => void;
  __writeTitle?: string | null;
};

function block(e: Event): void {
  if (!adminReadOnly.value) return;
  // Capture phase on the element itself runs before Vue's @click, and
  // stopImmediatePropagation keeps that handler from running at all. For a
  // submit button it also cancels the form's implicit submission on Enter.
  e.preventDefault();
  e.stopImmediatePropagation();
}

function sync(el: Guarded): void {
  if (adminReadOnly.value) {
    if (el.__writeTitle === undefined)
      el.__writeTitle = el.getAttribute("title");
    el.setAttribute("aria-disabled", "true");
    el.setAttribute("title", READ_ONLY_TITLE);
    el.classList.add("is-read-only");
    // Inline rather than a stylesheet rule: the directive is the one thing
    // every guarded control shares, wherever its component's styles live.
    el.style.opacity = "0.55";
    el.style.cursor = "not-allowed";
    // A text field that saves on change cannot be stopped by blocking a
    // click — typing is the gesture — so it is made read-only instead.
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      el.readOnly = true;
    }
  } else if (el.__writeTitle !== undefined) {
    el.removeAttribute("aria-disabled");
    if (el.__writeTitle === null) el.removeAttribute("title");
    else el.setAttribute("title", el.__writeTitle);
    el.classList.remove("is-read-only");
    el.style.opacity = "";
    el.style.cursor = "";
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
      el.readOnly = false;
    }
    el.__writeTitle = undefined;
  }
}

/** `v-write` — marks a control that changes something. */
export const vWrite: Directive<Guarded> = {
  mounted(el) {
    el.__writeBlock = block;
    el.addEventListener("click", block, { capture: true });
    el.__writeStop = watchEffect(() => sync(el));
  },
  updated(el) {
    sync(el);
  },
  unmounted(el) {
    el.__writeStop?.();
    if (el.__writeBlock)
      el.removeEventListener("click", el.__writeBlock, { capture: true });
  },
};
